import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";

// Import icons
import { Images } from "../../../utils/Images";

// Styled components for horizontal top bar
const TopBarContainer = styled(Box)(({ theme }) => ({
  background: "rgba(245, 245, 245, 0.95)", // Semi-transparent for blur effect
  backdropFilter: "blur(10px)", // Blur effect
  WebkitBackdropFilter: "blur(10px)", // Safari support
  borderRadius: "8px",
  padding: "8px",
  width: "100%",
  display: "flex",
  gap: "4px",
  justifyContent: "space-evenly",
  overflow: "auto",
  maxHeight: "60px",
  position: "sticky",
  top: "10px", // Leave gap above the top bar
  zIndex: 999, // Ensure it stays above other content
  alignSelf: "flex-start", // Helps with sticky positioning
  boxShadow: "0 12px 32px rgba(0, 0, 0, 0.35), 0 4px 16px rgba(0, 0, 0, 0.25), 0 2px 8px rgba(0, 0, 0, 0.2)", // Strong box shadow for maximum visibility
  "&::-webkit-scrollbar": {
    height: "4px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#C1C1C1",
    borderRadius: "2px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "#A1A1A1",
  },
  "@media (max-width: 460px)": {
    padding: "6px",
    gap: "2px",
    maxHeight: "50px",
  },
}));

const TopBarMenuItem = styled(Box)(({ theme, isSelected }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "12px 16px",
  cursor: "pointer",
  position: "relative",
  transition: "all 0.2s ease",
  borderRadius: "6px",
  backgroundColor: isSelected ? "#1D1D1B" : "transparent",
  color: isSelected ? "#FFFFFF" : "#1D1D1B",
  flex: "1",
  justifyContent: "center",
  minWidth: "100px",
  maxWidth: "150px",
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: isSelected ? "#1D1D1B" : "rgba(29, 29, 27, 0.1)",
  },
  "@media (max-width: 460px)": {
    gap: "4px",
    padding: "8px 10px",
    minWidth: "70px",
    maxWidth: "100px",
    borderRadius: "4px",
  },
}));

const IconContainer = styled(Box)(({ theme, isSelected }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "& img": {
    width: "18px",
    height: "18px",
    filter: isSelected ? "brightness(0) invert(1)" : "none",
    flexShrink: 0,
  },
  "@media (max-width: 460px)": {
    "& img": {
      width: "14px",
      height: "14px",
    },
  },
}));

const MenuText = styled(Typography)(({ theme, isSelected }) => ({
  fontFamily: "Red Hat Display",
  fontWeight: isSelected ? 600 : 400,
  fontStyle: isSelected ? "SemiBold" : "Regular",
  fontSize: "14px",
  lineHeight: "100%",
  letterSpacing: "0%",
  verticalAlign: "middle",
  color: isSelected ? "#FFFFFF" : "#1D1D1B",
  flexShrink: 0,
  "@media (max-width: 460px)": {
    fontSize: "11px",
  },
}));

const CampaignTopBar = ({ onNavigate, campaignType = "normal", showPatientHeader = false, campaignData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  // Define menu items based on campaign type
  const normalMenuItems = [
    {
      id: "products",
      label: "Products",
      icon: Images.box,
      show: campaignData?.products && Array.isArray(campaignData.products) && campaignData.products.length > 0,
    },
    {
      id: "project",
      label: "Projects",
      icon: Images.layer,
      show: campaignData?.projects && Array.isArray(campaignData.projects) && campaignData.projects.length > 0,
    },
    {
      id: "updates",
      label: "Updates",
      icon: Images.refresh,
      show: campaignData?.updates && Array.isArray(campaignData.updates) && campaignData.updates.length > 0,
    },
    {
      id: "comments",
      label: "Comments",
      icon: Images.messages,
      show: true, // Always show comments
    },
  ];

  const medicalMenuItems = [
    {
      id: "story",
      label: "Story",
      icon: Images.box,
      show: true, // Always show story for medical campaigns
    },
    {
      id: "document",
      label: "Document",
      icon: Images.layer,
      show: campaignData?.documents && Array.isArray(campaignData.documents) && campaignData.documents.length > 0,
    },
    {
      id: "updates",
      label: "Updates",
      icon: Images.refresh,
      show: campaignData?.updates && Array.isArray(campaignData.updates) && campaignData.updates.length > 0,
    },
    {
      id: "comments",
      label: "Comments",
      icon: Images.messages,
      show: true, // Always show on story page
    },
  ];

  // Filter menu items based on what should be shown
  const allMenuItems = campaignType === "medical" ? medicalMenuItems : normalMenuItems;
  const menuItems = allMenuItems.filter(item => item.show !== false);
  const [selectedItem, setSelectedItem] = useState(menuItems[0].id);

  // Intersection Observer to detect which section is in view
  useEffect(() => {
    let timeoutId = null;
    let scrollTimeoutId = null;
    
    // Function to find the visible element (handles duplicate IDs for desktop/mobile)
    const findVisibleElement = (id) => {
      // Get all elements with this ID (there might be duplicates for desktop/mobile)
      const allElements = Array.from(document.querySelectorAll(`#${id}`));
      
      // Find the visible one (not hidden by CSS)
      const visibleElement = allElements.find(el => {
        const style = window.getComputedStyle(el);
        const rect = el.getBoundingClientRect();
        return (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.opacity !== '0' &&
          rect.width > 0 &&
          rect.height > 0
        );
      });
      
      // If no visible element found, return the first one (fallback)
      return visibleElement || allElements[0] || document.getElementById(id);
    };
    
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -50% 0px',
      threshold: [0, 0.1, 0.5, 1.0]
    };

    const observerCallback = (entries) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        const sectionPositions = menuItems.map(item => {
          const element = findVisibleElement(item.id);
          if (element) {
            const rect = element.getBoundingClientRect();
            return {
              id: item.id,
              top: rect.top,
              bottom: rect.bottom,
              height: rect.height,
              isVisible: rect.top < window.innerHeight && rect.bottom > 0
            };
          }
          return null;
        }).filter(Boolean);

        if (sectionPositions.length > 0) {
          let bestSection = null;
          let bestScore = -1;

          sectionPositions.forEach(section => {
            if (section.isVisible) {
              const visibleHeight = Math.min(section.bottom, window.innerHeight) - Math.max(section.top, 0);
              const visibilityRatio = visibleHeight / section.height;
              
              const topDistance = Math.max(0, section.top);
              const topScore = Math.max(0, 1 - (topDistance / window.innerHeight));
              
              const score = visibilityRatio + (topScore * 0.3);
              
              if (score > bestScore) {
                bestScore = score;
                bestSection = section.id;
              }
            }
          });

          if (bestSection && selectedItem !== bestSection) {
            setSelectedItem(bestSection);
          }
        }
      }, 100);
    };

    const updateSelectedItem = () => {
      const sectionPositions = menuItems.map(item => {
        const element = findVisibleElement(item.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          return {
            id: item.id,
            top: rect.top,
            bottom: rect.bottom,
            height: rect.height,
            isVisible: rect.top < window.innerHeight && rect.bottom > 0
          };
        }
        return null;
      }).filter(Boolean);

      if (sectionPositions.length > 0) {
        let bestSection = null;
        let bestScore = -1;

        sectionPositions.forEach(section => {
          if (section.isVisible) {
            const visibleHeight = Math.min(section.bottom, window.innerHeight) - Math.max(section.top, 0);
            const visibilityRatio = visibleHeight / section.height;
            
            const topDistance = Math.max(0, section.top);
            const topScore = Math.max(0, 1 - (topDistance / window.innerHeight));
            
            const score = visibilityRatio + (topScore * 0.3);
            
            if (score > bestScore) {
              bestScore = score;
              bestSection = section.id;
            }
          }
        });

        if (bestSection && selectedItem !== bestSection) {
          setSelectedItem(bestSection);
        }
      }
    };

    const handleScroll = () => {
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }
      scrollTimeoutId = setTimeout(updateSelectedItem, 50);
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all visible elements
    menuItems.forEach((item) => {
      const element = findVisibleElement(item.id);
      if (element) {
        observer.observe(element);
      }
    });

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }
    };
  }, [menuItems, selectedItem]);

  const handleItemClick = (itemId) => {
    console.log("🔵 handleItemClick called with itemId:", itemId);
    setSelectedItem(itemId);
    
    if (onNavigate) {
      console.log("🔵 Using onNavigate callback");
      onNavigate(itemId);
    } else {
      // Fallback: direct scroll if onNavigate is not provided
      const element = document.getElementById(itemId);
      console.log("🔵 Element found:", element ? "YES" : "NO", "for id:", itemId);
      
      if (element) {
        // Get current scroll position
        const scrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
        
        // Get the element's bounding rect (relative to viewport)
        const rect = element.getBoundingClientRect();
        
        // Calculate absolute position of element from top of document
        // rect.top is relative to viewport, so add current scroll to get absolute position
        const elementTop = rect.top + scrollTop;
        
        // Calculate offset (accounting for any fixed headers or spacing)
        const offset = isMobile ? 100 : 120;
        const targetScrollPosition = elementTop - offset;
        
        // Check if we're already close to the target (within 10px tolerance)
        const currentDistance = Math.abs(scrollTop - targetScrollPosition);
        const isAlreadyAtTarget = currentDistance < 10;
        
        console.log("🔵 Scroll Debug Info:", {
          itemId,
          elementTop: elementTop.toFixed(2),
          currentScrollTop: scrollTop.toFixed(2),
          rectTop: rect.top.toFixed(2),
          rectBottom: rect.bottom.toFixed(2),
          elementHeight: rect.height.toFixed(2),
          offset,
          targetScrollPosition: targetScrollPosition.toFixed(2),
          currentDistance: currentDistance.toFixed(2),
          isAlreadyAtTarget,
          isMobile,
          windowInnerHeight: window.innerHeight,
        });
        
        if (isAlreadyAtTarget) {
          console.log("🔵 Already at target position, skipping scroll");
          return;
        }
        
        // Ensure we don't scroll to negative position
        const finalPosition = Math.max(0, targetScrollPosition);
        
        console.log("🔵 Scrolling to position:", finalPosition);
        
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          window.scrollTo({
            top: finalPosition,
            behavior: "smooth",
          });
        });
      } else {
        console.error("❌ Element not found for id:", itemId);
        console.log("🔵 Available elements with IDs:", menuItems.map(item => {
          const el = document.getElementById(item.id);
          return { id: item.id, exists: !!el, elementTop: el ? (el.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0)).toFixed(2) : "N/A" };
        }));
      }
    }
  };

  return (
    <TopBarContainer>
      {menuItems.map((item) => {
        const isSelected = selectedItem === item.id;
        
        return (
          <TopBarMenuItem
            key={item.id}
            isSelected={isSelected}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleItemClick(item.id);
            }}
          >
            <IconContainer isSelected={isSelected}>
              <img src={item.icon} alt={item.label} />
            </IconContainer>
            <MenuText isSelected={isSelected}>
              {item.label}
            </MenuText>
          </TopBarMenuItem>
        );
      })}
    </TopBarContainer>
  );
};

export default CampaignTopBar;

